// Rooms & Inventory — Content-manager edit page.
// Backed by GET/PATCH /api/admin/rooms and POST /api/admin/rooms/{slug}/publish
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BedDouble, Save, X, Eye, EyeOff, DollarSign } from "lucide-react";
import { adminApi } from "@/lib/adminAuth";

function fmt(n) { return `$${Number(n || 0).toFixed(0)}`; }

function RoomEditor({ room, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: room.name || "",
        tagline: room.tagline || "",
        description: room.description || "",
        price_per_night: room.price_per_night || 0,
        occupancy: room.occupancy || 2,
        beds: room.beds || "",
        bathrooms: room.bathrooms || 1,
        view: room.view || "",
        hero_image: room.hero_image || "",
        hero_video: room.hero_video || "",
    });
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            await adminApi.patch(`/admin/rooms/${room.slug}`, {
                ...form,
                price_per_night: Number(form.price_per_night),
                occupancy: Number(form.occupancy),
                bathrooms: Number(form.bathrooms),
            });
            toast.success("Room updated");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save room");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="room-editor" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-navy/10">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Edit room</div>
                        <h2 className="font-display text-2xl text-navy mt-1">{room.name}</h2>
                    </div>
                    <button onClick={onClose} data-testid="room-editor-close" className="p-2 rounded-full hover:bg-navy/5"><X size={18} /></button>
                </div>
                <div className="p-6 space-y-4">
                    <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="rm-name" />
                    <Field label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testid="rm-tagline" />
                    <Field label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea testid="rm-desc" />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Field label="Price / night (USD)" type="number" value={form.price_per_night} onChange={(v) => setForm({ ...form, price_per_night: v })} testid="rm-price" />
                        <Field label="Sleeps" type="number" value={form.occupancy} onChange={(v) => setForm({ ...form, occupancy: v })} testid="rm-occ" />
                        <Field label="Beds" value={form.beds} onChange={(v) => setForm({ ...form, beds: v })} testid="rm-beds" />
                        <Field label="Bathrooms" type="number" value={form.bathrooms} onChange={(v) => setForm({ ...form, bathrooms: v })} testid="rm-baths" />
                    </div>
                    <Field label="View" value={form.view} onChange={(v) => setForm({ ...form, view: v })} testid="rm-view" />
                    <Field label="Hero image URL" value={form.hero_image} onChange={(v) => setForm({ ...form, hero_image: v })} testid="rm-hero-image" />
                    <Field label="Hero video URL" value={form.hero_video} onChange={(v) => setForm({ ...form, hero_video: v })} testid="rm-hero-video" />
                </div>
                <div className="sticky bottom-0 bg-white border-t border-navy/10 flex items-center justify-end gap-3 px-6 py-4">
                    <button onClick={onClose} className="px-5 py-2.5 text-sm text-navy hover:bg-navy/5 rounded-full">Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="rm-save" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-navy text-ivory text-[11px] uppercase tracking-[0.16em] hover:bg-navy/90 disabled:opacity-50">
                        <Save size={13} /> {busy ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", textarea, testid }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">{label}</span>
            {textarea ? (
                <textarea data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={4}
                    className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
            ) : (
                <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                    className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
            )}
        </label>
    );
}

export default function AdminRooms() {
    const [rooms, setRooms] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await adminApi.get("/admin/rooms");
            setRooms(data);
        } catch (e) {
            toast.error("Could not load rooms");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const togglePublish = async (slug) => {
        try {
            const { data } = await adminApi.post(`/admin/rooms/${slug}/publish`);
            toast.success(data.published ? "Room is now published" : "Room hidden from public site");
            load();
        } catch { toast.error("Could not toggle publish"); }
    };

    return (
        <div data-testid="admin-rooms-page">
            <header className="mb-8">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Inventory</div>
                <h1 className="font-display text-4xl md:text-5xl text-[#0b1f22] mt-3">Rooms & rates</h1>
                <p className="text-[#0b1f22]/60 mt-2 text-sm">
                    Edit room descriptions, nightly rates and photos. Toggle publish to hide a room from the public site without deleting it.
                </p>
            </header>

            {loading ? (
                <div className="text-navy/60 text-sm">Loading rooms…</div>
            ) : (
                <div className="bg-white rounded-2xl border border-navy/10 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-navy/5 text-[10px] uppercase tracking-[0.18em] text-navy/60">
                            <tr>
                                <th className="text-left px-5 py-3">Room</th>
                                <th className="text-left px-5 py-3 hidden md:table-cell">Beds</th>
                                <th className="text-left px-5 py-3 hidden md:table-cell">Sleeps</th>
                                <th className="text-left px-5 py-3">Rate</th>
                                <th className="text-left px-5 py-3">Status</th>
                                <th className="text-right px-5 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rooms.map((r) => (
                                <tr key={r.slug} data-testid={`room-row-${r.slug}`} className="border-t border-navy/10">
                                    <td className="px-5 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-navy/5 shrink-0">
                                                {r.hero_image && <img src={r.hero_image} alt="" className="w-full h-full object-cover" />}
                                            </div>
                                            <div>
                                                <div className="font-medium text-navy">{r.name}</div>
                                                <div className="text-[11px] text-navy/50 mt-0.5">{r.view}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 text-navy/70 hidden md:table-cell">{r.beds}</td>
                                    <td className="px-5 py-4 text-navy/70 hidden md:table-cell">{r.occupancy}</td>
                                    <td className="px-5 py-4">
                                        <span className="font-display text-lg text-navy inline-flex items-center gap-1"><DollarSign size={13} className="text-gold" />{fmt(r.price_per_night).replace("$","")}</span>
                                        <span className="text-[11px] text-navy/50 ml-1">/night</span>
                                    </td>
                                    <td className="px-5 py-4">
                                        {r.published !== false ? (
                                            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-jade/15 text-jade">
                                                <Eye size={10} /> Published
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-navy/10 text-navy/60">
                                                <EyeOff size={10} /> Hidden
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-4 text-right">
                                        <div className="inline-flex items-center gap-2">
                                            <button
                                                onClick={() => togglePublish(r.slug)}
                                                data-testid={`room-publish-${r.slug}`}
                                                className="text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 border border-navy/20 rounded-full text-navy hover:bg-navy hover:text-ivory transition-colors"
                                            >
                                                {r.published !== false ? "Hide" : "Publish"}
                                            </button>
                                            <button
                                                onClick={() => setEditing(r)}
                                                data-testid={`room-edit-${r.slug}`}
                                                className="text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full bg-navy text-ivory hover:bg-navy/90"
                                            >
                                                Edit
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {rooms.length === 0 && (
                                <tr><td colSpan={6} className="px-5 py-8 text-center text-navy/50">
                                    <BedDouble size={20} className="mx-auto mb-2 text-navy/30" /> No rooms yet
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && <RoomEditor room={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}
